import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  Settings,
  Layers3,
  ClipboardList,
  FileText,
  Network,
  Tags,
  Users,
  GitBranch,
  RefreshCcw,
  ArrowRight,
  Briefcase
} from 'lucide-react';
import api from '../../api';

const MODULE_DEFINITIONS = [
  {
    domain: 'master',
    key: 'companies',
    label: 'Companies',
    description: 'Master list of companies and business partners.',
    path: '/masterdata-company',
    color: '#1D4ED8',
    softColor: 'rgba(29, 78, 216, 0.10)',
    icon: Building2,
    request: () => api.get('/md/companies-page', { params: { page: 0, size: 100 } }),
    getItemLabel: (item) => item.companyName || item.name || item.company || `Company #${item.id}`,
    getItemMeta: (item) => item.entityType?.entityType || item.entityType || item.source?.name || 'Company record',
    getBreakdownLabel: (item) => item.entityType?.entityType || item.entityType || 'Unassigned Entity Type',
    breakdownTitle: 'Entity Type Mix'
  },
  {
    domain: 'master',
    key: 'services',
    label: 'Services',
    description: 'Service master data used across standards and audit rules.',
    path: '/masterdata-services',
    color: '#0F766E',
    softColor: 'rgba(15, 118, 110, 0.10)',
    icon: Settings,
    request: () => api.get('/md/company-service-page', { params: { page: 0, size: 100 } }),
    getItemLabel: (item) => item.name || item.serviceName || `Service #${item.id}`,
    getItemMeta: () => 'Service catalog',
    getBreakdownLabel: (item) => item.name || item.serviceName || 'Unnamed Service',
    breakdownTitle: 'Top Services Loaded'
  },
  {
    domain: 'master',
    key: 'schemeCategory',
    label: 'Scheme Category',
    description: 'Certification scheme categories used in master data.',
    path: '/masterdata-scheme-category',
    color: '#7C3AED',
    softColor: 'rgba(124, 58, 237, 0.10)',
    icon: Layers3,
    request: () => api.get('/md/company-certscheme-page', { params: { page: 0, size: 100 } }),
    getItemLabel: (item) => item.name || item.scheme || `Scheme #${item.id}`,
    getItemMeta: () => 'Scheme category',
    getBreakdownLabel: (item) => item.name || item.scheme || 'Unnamed Scheme',
    breakdownTitle: 'Top Scheme Categories'
  },
  {
    domain: 'master',
    key: 'auditType',
    label: 'Audit Type / Service Item',
    description: 'Audit type and service item combinations.',
    path: '/masterdata-audittype',
    color: '#DC2626',
    softColor: 'rgba(220, 38, 38, 0.10)',
    icon: ClipboardList,
    request: () => api.get('/md/audit-type/page', { params: { page: 0, size: 100 } }),
    getItemLabel: (item) => item.auditType || item.name || item.serviceItem || `Audit Type #${item.id}`,
    getItemMeta: (item) => item.service?.name || item.serviceName || item.certificationScheme?.name || 'Service item',
    getBreakdownLabel: (item) => item.service?.name || item.serviceName || item.certificationScheme?.name || 'Unassigned Service',
    breakdownTitle: 'Audit Type by Service'
  },
  {
    domain: 'master',
    key: 'standard',
    label: 'Standard',
    description: 'Standards connected to schemes, services, and sectors.',
    path: '/masterdata-standard',
    color: '#2563EB',
    softColor: 'rgba(37, 99, 235, 0.10)',
    icon: FileText,
    request: () => api.get('/md/standards/page', { params: { page: 0, size: 100 } }),
    getItemLabel: (item) => item.name || item.standard || item.code || `Standard #${item.id}`,
    getItemMeta: (item) => item.version ? `Version ${item.version}` : (item.code || 'Standard master'),
    getBreakdownLabel: (item) => item.version ? `Version ${item.version}` : 'No Version',
    breakdownTitle: 'Standard Version Mix'
  },
  {
    domain: 'master',
    key: 'sector',
    label: 'Sector',
    description: 'Sector master data for standards and competencies.',
    path: '/masterdata-sector',
    color: '#0891B2',
    softColor: 'rgba(8, 145, 178, 0.10)',
    icon: Network,
    request: () => api.get('/md/sectors-page', { params: { page: 0, size: 100 } }),
    getItemLabel: (item) => item.name || item.sector || `Sector #${item.id}`,
    getItemMeta: (item) => item.code || 'Sector master',
    getBreakdownLabel: (item) => item.code || 'No Code',
    breakdownTitle: 'Sector Code Mix'
  },
  {
    domain: 'master',
    key: 'tagging',
    label: 'Tagging',
    description: 'Reusable tagging master data across modules.',
    path: '/masterdata-tagging',
    color: '#D97706',
    softColor: 'rgba(217, 119, 6, 0.10)',
    icon: Tags,
    request: () => api.get('/md/tagging-page', { params: { page: 0, size: 100 } }),
    getItemLabel: (item) => item.name || item.tagName || `Tag #${item.id}`,
    getItemMeta: (item) => item.moduleKey || item.description || 'Tagging master',
    getBreakdownLabel: (item) => item.moduleKey || 'General',
    breakdownTitle: 'Tagging by Module'
  },
  {
    domain: 'audit',
    key: 'personnelQualification',
    label: 'Personnel Qualification',
    description: 'Audit qualification records and level mapping.',
    path: '/masterdata-personnel-qualification',
    color: '#0EA5E9',
    softColor: 'rgba(14, 165, 233, 0.10)',
    icon: Users,
    request: () => api.get('/audit/personnel-qualifications'),
    getItemLabel: (item) => item.qualification || item.name || `Qualification #${item.id}`,
    getItemMeta: (item) => item.qualificationGroup || item.role || 'Qualification record',
    getBreakdownLabel: (item) => item.qualificationGroup || 'Ungrouped',
    breakdownTitle: 'Qualification Group Mix'
  },
  {
    domain: 'audit',
    key: 'competency',
    label: 'Competency',
    description: 'Competency request records in audit workflow.',
    path: '/masterdata-audit-competency',
    color: '#F97316',
    softColor: 'rgba(249, 115, 22, 0.10)',
    icon: ClipboardList,
    request: () => api.get('/audit/competencies/requests'),
    getItemLabel: (item) => item.name || item.userName || item.requestType || `Competency #${item.id}`,
    getItemMeta: (item) => item.status || item.qualificationName || 'Competency request',
    getBreakdownLabel: (item) => item.status || 'Unknown Status',
    breakdownTitle: 'Request Status Mix'
  },
  {
    domain: 'audit',
    key: 'impartiality',
    label: 'Impartiality',
    description: 'Impartiality rules for audit assignment conflicts.',
    path: '/masterdata-audit-impartiality',
    color: '#8B5CF6',
    softColor: 'rgba(139, 92, 246, 0.10)',
    icon: FileText,
    request: () => api.get('/md/impartiality-rules'),
    getItemLabel: (item) => item.name || `Rule #${item.id}`,
    getItemMeta: (item) => item.description || 'Impartiality rule',
    getBreakdownLabel: (item) => item.standard || item.serviceName || 'General Rule',
    breakdownTitle: 'Rule by Service'
  },
  {
    domain: 'audit',
    key: 'workflow',
    label: 'Audit Workflow',
    description: 'Approval and review workflow templates for audit.',
    path: '/masterdata-audit-workflow',
    color: '#EC4899',
    softColor: 'rgba(236, 72, 153, 0.10)',
    icon: GitBranch,
    request: () => api.get('/audit/workflows', { params: { page: 0, size: 100 } }),
    getItemLabel: (item) => item.workflowName || item.name || `Workflow #${item.id}`,
    getItemMeta: (item) => `${Array.isArray(item.steps) ? item.steps.length : 0} steps`,
    getBreakdownLabel: (item) => `${Array.isArray(item.steps) ? item.steps.length : 0} steps`,
    breakdownTitle: 'Workflow by Steps'
  },
  {
    domain: 'master',
    key: 'users',
    label: 'Users',
    description: 'Internal users available in master data.',
    path: '/masterdata-list-users',
    color: '#4F46E5',
    softColor: 'rgba(79, 70, 229, 0.10)',
    icon: Users,
    request: () => api.get('/md/internal-users', { params: { page: 0, size: 1000 } }),
    getItemLabel: (item) => `${item.firstName || ''} ${item.lastName || ''}`.trim() || item.email || `User #${item.id}`,
    getItemMeta: (item) => item.email || item.jobTitle?.name || item.role || 'Internal user',
    breakdownTitle: 'User Status'
  },
  {
    domain: 'master',
    key: 'jobTitle',
    label: 'Job Title',
    description: 'Job title master data and menu access permissions.',
    path: '/masterdata-jobtitle',
    color: '#16A34A',
    softColor: 'rgba(22, 163, 74, 0.10)',
    icon: Briefcase,
    request: () => api.get('/md/job-title-page', { params: { page: 0, size: 100 } }),
    getItemLabel: (item) => item.name || `Job Title #${item.id}`,
    getItemMeta: (item) => item.departementName || 'Job title master',
    getBreakdownLabel: (item) => item.departementName || 'Unassigned Department',
    breakdownTitle: 'Job Title by Department'
  },
  {
    domain: 'master',
    key: 'branch',
    label: 'Branch',
    description: 'Branch master data used for operational assignment.',
    path: '/masterdata-branch',
    color: '#059669',
    softColor: 'rgba(5, 150, 105, 0.10)',
    icon: GitBranch,
    request: () => api.get('/branches'),
    getItemLabel: (item) => item.name || item.branchName || item.code || `Branch #${item.id}`,
    getItemMeta: (item) => item.code || item.location || 'Branch master',
    getBreakdownLabel: (item) => item.code || item.name || 'No Code',
    breakdownTitle: 'Branch Distribution'
  }
];

const extractItems = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return [];
};

const extractTotal = (payload, fallbackCount) => {
  const total = payload?.totalElements ?? payload?.total ?? payload?.count ?? payload?.data?.totalElements ?? payload?.data?.total;
  return Number.isFinite(Number(total)) ? Number(total) : fallbackCount;
};

const ModuleCountCard = ({ module, loading }) => {
  const Icon = module.icon;

  return (
    <div className="card border-0 shadow-sm h-100">
      <div className="card-body d-flex flex-column">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div className="small text-uppercase text-muted fw-semibold">{module.label}</div>
          <Icon size={16} color={module.color} />
        </div>

        <div className="h1 mb-1" style={{ color: module.color }}>{loading ? '...' : module.countLabel}</div>
        <div className="text-muted small mb-3" style={{ minHeight: 36 }}>{module.description}</div>

        <div className="mt-auto d-flex justify-content-between align-items-center">
          <span className="small text-muted">{module.countHint}</span>
          <Link to={module.path} className="small fw-semibold text-decoration-none">
            Open
            <ArrowRight size={12} className="ms-1" />
          </Link>
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [moduleData, setModuleData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const fetchDashboardData = async () => {
    setLoading(true);
    setErrorMessage('');

    const results = await Promise.allSettled(MODULE_DEFINITIONS.map((module) => module.request()));
    const failedLabels = [];

    const mappedModules = MODULE_DEFINITIONS.map((module, index) => {
      const result = results[index];

      if (result.status !== 'fulfilled') {
        failedLabels.push(module.label);
        return {
          ...module,
          count: 0,
          activeCount: module.key === 'users' ? 0 : null,
          countLabel: module.key === 'users' ? '0/0' : '0',
          countHint: module.key === 'users' ? 'active / total users' : 'records',
          items: [],
          loaded: false
        };
      }

      const payload = result.value?.data;
      const items = extractItems(payload);
      const totalCount = extractTotal(payload, items.length);
      const activeCount = module.key === 'users'
        ? items.filter((item) => String(item.status ?? '') === '1').length
        : null;

      return {
        ...module,
        count: totalCount,
        activeCount,
        countLabel: module.key === 'users' ? `${activeCount}/${totalCount}` : `${totalCount}`,
        countHint: module.key === 'users' ? 'active / total users' : 'records',
        items,
        loaded: true
      };
    });

    setModuleData(mappedModules);
    if (failedLabels.length) {
      setErrorMessage(
        failedLabels.length === MODULE_DEFINITIONS.length
          ? 'Failed to load dashboard data.'
          : `Some modules could not be loaded: ${failedLabels.join(', ')}.`
      );
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const masterModules = useMemo(
    () => moduleData.filter((module) => module.domain === 'master'),
    [moduleData]
  );
  const auditModules = useMemo(
    () => moduleData.filter((module) => module.domain === 'audit'),
    [moduleData]
  );
  const loadedModulesCount = moduleData.filter((module) => module.loaded).length;
  const usersModule = moduleData.find((module) => module.key === 'users');
  const masterRecords = masterModules.reduce((sum, module) => sum + module.count, 0);
  const auditRecords = auditModules.reduce((sum, module) => sum + module.count, 0);

  return (
    <>
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <div className="d-flex align-items-center gap-2 mb-1">
                <LayoutDashboard size={22} className="text-primary" />
                <h2 className="page-title mb-0">Master Data Dashboard</h2>
              </div>
              <div className="text-muted">
                Overview analytics domain Master Data dan Audit Data, dengan chart count per modul.
              </div>
            </div>
            <div className="col-auto ms-auto d-print-none">
              <button className="btn btn-outline-primary" onClick={fetchDashboardData}>
                <RefreshCcw size={16} className="me-2" />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          {errorMessage && (
            <div className={`alert ${loadedModulesCount > 0 ? 'alert-warning' : 'alert-danger'}`} role="alert">
              {errorMessage}
            </div>
          )}

          <div className="row row-cards mb-3">
            {[
              {
                label: 'Modules Tracked',
                value: moduleData.length,
                hint: 'Total modul yang dipantau pada dashboard'
              },
              {
                label: 'Master Data Records',
                value: loading ? '...' : masterRecords,
                hint: 'Akumulasi jumlah data modul non-Audit menu'
              },
              {
                label: 'Audit Analytics Records',
                value: loading ? '...' : auditRecords,
                hint: 'Akumulasi jumlah data modul di menu Audit'
              },
              {
                label: 'Users Active / Total',
                value: loading ? '...' : (usersModule?.countLabel || '0/0'),
                hint: loading ? 'Checking status...' : 'Internal user status summary'
              }
            ].map((card) => (
              <div key={card.label} className="col-sm-6 col-xl-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="text-uppercase text-muted small fw-semibold">{card.label}</div>
                    <div className="h2 mt-2 mb-1">{card.value}</div>
                    <div className="text-muted small">{card.hint}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="row row-cards">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header">
                  <div>
                    <h3 className="card-title mb-1">Master Data</h3>
                    <div className="text-muted small">Modul di luar menu Audit. Setiap modul ditampilkan terpisah dengan chart count sendiri.</div>
                  </div>
                </div>
                <div className="card-body">
                  {masterModules.length > 0 ? (
                    <div className="row row-cards">
                      {masterModules.map((module) => (
                        <div key={module.key} className="col-sm-6 col-lg-4 col-xl-3">
                          <ModuleCountCard
                            module={module}
                            loading={loading}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-muted">No master data modules available.</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="row row-cards mt-1">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header">
                  <div>
                    <h3 className="card-title mb-1">Audit Data</h3>
                    <div className="text-muted small">Modul menu Audit: Personnel Qualification, Competency, Impartiality, dan Workflow.</div>
                  </div>
                </div>
                <div className="card-body">
                  {auditModules.length > 0 ? (
                    <div className="row row-cards">
                      {auditModules.map((module) => (
                        <div key={module.key} className="col-sm-6 col-lg-4 col-xl-3">
                          <ModuleCountCard
                            module={module}
                            loading={loading}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-muted">No audit modules available.</div>
                  )}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default Dashboard;
